import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPushupRepCounter } from './PushupRepCounter';
import { createSetCompletionGate, deriveCircuitPosition } from './AiSupervisedCircuit';
import { MediaPipePoseProvider } from './MediaPipePoseProvider';
import { ArrowPathIcon } from '@heroicons/react/24/solid';
import { finishWithPosition } from '../../components/activities/activityPosition';
import { SUBMIT_NOUNS, SubmitFeedbackAnnouncement, SubmitFeedbackButton, SubmitFeedbackProgress } from '../../components/activities/SubmitFeedback';
import './AiSupervisedWorkoutPlayer.css';

const CONNECTIONS = [['shoulder', 'elbow'], ['elbow', 'wrist'], ['shoulder', 'hip'], ['hip', 'knee'], ['knee', 'ankle']];

function drawOverlay(canvas, video, observation) {
  if (!canvas || !video || !observation || process.env.NODE_ENV === 'test') return;
  const width = video.videoWidth || 640;
  const height = video.videoHeight || 480;
  if (canvas.width !== width) canvas.width = width;
  if (canvas.height !== height) canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) return;
  context.clearRect(0, 0, width, height);
  Object.entries(observation.sides || {}).forEach(([sideName, side]) => {
    context.strokeStyle = sideName === 'left' ? '#22d3ee' : '#a78bfa';
    context.fillStyle = context.strokeStyle;
    context.lineWidth = 3;
    CONNECTIONS.forEach(([from, to]) => {
      if (!side[from] || !side[to]) return;
      context.beginPath();
      context.moveTo(side[from].x * width, side[from].y * height);
      context.lineTo(side[to].x * width, side[to].y * height);
      context.stroke();
    });
    Object.values(side).forEach(point => {
      if (!point || point.confidence < 0.5) return;
      context.beginPath();
      context.arc(point.x * width, point.y * height, 4, 0, Math.PI * 2);
      context.fill();
    });
  });
}

function errorMessage(error) {
  if (error?.name === 'NotAllowedError') return 'Camera permission was denied. Allow camera access, then retry.';
  if (error?.name === 'NotFoundError') return 'No camera was found on this device.';
  return error?.message || 'Camera or pose model setup failed. Retry, or use a normal workout.';
}

export default function AiSupervisedWorkoutPlayer({
  activity,
  poseMetadata,
  backendService,
  onActivityRefresh,
  onActivityComplete,
  onReady,
  providerFactory = () => new MediaPipePoseProvider(),
  readOnly = false,
}) {
  const [localActivity, setLocalActivity] = useState(activity);
  const [cameraStatus, setCameraStatus] = useState('idle');
  const [diagnostic, setDiagnostic] = useState(null);
  const [error, setError] = useState('');
  const [completionError, setCompletionError] = useState('');
  const [completionPending, setCompletionPending] = useState(false);
  const [completionStage, setCompletionStage] = useState(null);
  const [completionSaved, setCompletionSaved] = useState(false);
  const [completionRetrying, setCompletionRetrying] = useState(false);
  const [refreshError, setRefreshError] = useState('');
  const completionScopeRef = useRef(null);
  const completingActivityRef = useRef(false);
  const completionRunRef = useRef(null);
  const completionOwner = backendService?.positionOwner;
  const completionIdentity = backendService?.authenticatedIdentity;
  // A new completion cycle: abandons the old one without letting it publish.
  const resetCompletion = useCallback(() => {
    completionScopeRef.current?.abort();
    completionScopeRef.current = new AbortController();
    completingActivityRef.current = false;
    setCompletionPending(false);
    setCompletionStage(null);
    setCompletionSaved(false);
    setCompletionRetrying(false);
    setCompletionError('');
    setRefreshError('');
  }, []);
  useLayoutEffect(() => {
    resetCompletion();
    // Reopening can install a successor scope without replacing this effect.
    return () => completionScopeRef.current?.abort();
  }, [resetCompletion, backendService, completionOwner, completionIdentity, activity._id, readOnly]);
  const completionStatusRef = useRef(activity.status);
  useLayoutEffect(() => {
    const previousStatus = completionStatusRef.current;
    completionStatusRef.current = activity.status;
    // Only a genuine reopen of a completed workout starts a new cycle; the
    // optimistic completed status belongs to the in-flight completion.
    if (previousStatus === 'complete' && activity.status !== 'complete') resetCompletion();
  }, [activity.status, resetCompletion]);
  const [repState, setRepState] = useState({ count: 0, phase: 'awaiting_top', selectedSide: null, lastRep: null });
  const [formFeedback, setFormFeedback] = useState('');
  const [now, setNow] = useState(Date.now());
  const [referenceImages, setReferenceImages] = useState([]);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const providerRef = useRef(null);
  const counterRef = useRef(null);
  const positionRef = useRef(null);
  const activityRef = useRef(localActivity);
  const completionGateRef = useRef(createSetCompletionGate());
  const previousStatusRef = useRef(activity.status);
  const surfacedRepRef = useRef(null);
  const observationHandlerRef = useRef(null);
  const onReadyRef = useRef(onReady);
  const workSessionRef = useRef({ key: null, startedAtMs: null });

  useEffect(() => {
    setLocalActivity(activity);
    activityRef.current = activity;
  }, [activity]);

  const position = useMemo(() => {
    const persistedPosition = deriveCircuitPosition(localActivity, now);
    if (localActivity.status !== 'ongoing' || persistedPosition.kind !== 'work') return persistedPosition;
    const sessionKey = `${localActivity._id}:${persistedPosition.workout_set_id}`;
    if (workSessionRef.current.key !== sessionKey) {
      workSessionRef.current = { key: sessionKey, startedAtMs: now };
    }
    return deriveCircuitPosition(localActivity, now, {
      workSessionStartedAtMs: workSessionRef.current.startedAtMs,
    });
  }, [localActivity, now]);
  const canInteract = !readOnly && (localActivity.status === 'pending' || localActivity.status === 'ongoing');
  useEffect(() => { positionRef.current = position; }, [position]);
  useEffect(() => { activityRef.current = localActivity; }, [localActivity]);
  useEffect(() => { onReadyRef.current = onReady; }, [onReady]);

  useEffect(() => {
    if (!canInteract || (position.kind !== 'rest' && !(position.kind === 'work' && Number.isFinite(position.remainingSeconds)))) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(timer);
  }, [canInteract, position.kind, position.stepIndex, position.remainingSeconds]);

  useEffect(() => {
    providerRef.current?.setInferenceEnabled?.(canInteract && cameraStatus === 'ready' && localActivity.status === 'ongoing' && position.kind === 'work');
  }, [canInteract, cameraStatus, localActivity.status, position.kind, position.stepIndex]);

  useEffect(() => {
    if (!poseMetadata) return;
    counterRef.current = createPushupRepCounter(poseMetadata);
    surfacedRepRef.current = null;
    if (position.kind === 'work') setFormFeedback('');
    setRepState({ count: 0, phase: 'awaiting_top', selectedSide: null, lastRep: null });
  }, [poseMetadata, position.kind, position.stepIndex]);

  useEffect(() => {
    let cancelled = false;
    const paths = [poseMetadata?.key_moments?.top?.reference_image, poseMetadata?.key_moments?.bottom?.reference_image].filter(Boolean);
    if (!backendService?.fetchExerciseImage || paths.length === 0) return undefined;
    Promise.all(paths.map(path => backendService.fetchExerciseImage(path)))
      .then(images => { if (!cancelled) setReferenceImages(images); })
      .catch(() => { if (!cancelled) setReferenceImages([]); });
    return () => { cancelled = true; };
  }, [backendService, poseMetadata]);

  const finishWorkout = useCallback(async (explicit = false) => {
    // A scope reset can render once with the previous circuit position. Admit
    // completion from the current activity bytes, never that stale projection.
    if (!canInteract || completingActivityRef.current
      || deriveCircuitPosition(activityRef.current, Date.now()).kind !== 'complete') return;
    completingActivityRef.current = true;
    const scope = completionScopeRef.current;
    // The rendered owner, like manual Activity: a callback from a stale owner's render is never current.
    const isCurrent = () => !scope.signal.aborted
      && completionOwner === backendService.positionOwner && completionIdentity === backendService.authenticatedIdentity;
    const activityId = activityRef.current._id;
    setCompletionPending(true);
    setCompletionStage('checking');
    setCompletionRetrying(explicit);
    setCompletionError('');
    // An abandoned cycle's request may still be joined by activityPosition; let
    // it settle first so this cycle starts its own completion intent.
    const previousRun = completionRunRef.current;
    let releaseRun;
    const run = new Promise(resolve => { releaseRun = resolve; });
    completionRunRef.current = run;
    // Persisted: refresh failures are reported, never retried as a second completion.
    const finishSaved = async () => {
      setCompletionSaved(true);
      setCompletionStage('finishing');
      try {
        await onActivityComplete?.(activityId);
      } catch (completeError) {
        if (isCurrent()) setRefreshError(`Your workout was saved, but refreshing the view failed. Refresh the page to see it. ${completeError?.message || ''}`.trim());
      }
    };
    try {
      if (previousRun) await previousRun;
      if (!isCurrent()) return;
      const completed = await finishWithPosition(backendService, activityId, navigator.geolocation, {
        signal: scope.signal, acquirePosition: explicit, isCurrent,
        onStage: stage => { if (isCurrent()) setCompletionStage(stage); },
        beforeCompletion: () => {
          if (isCurrent()) setCompletionStage('saving');
          return backendService.waitForWorkoutSetUpdates?.();
        },
      });
      if (!isCurrent()) return;
      if (completed) {
        await providerRef.current?.stop?.();
        if (!isCurrent()) return;
        providerRef.current = null;
        setCameraStatus('idle');
        setDiagnostic(null);
        await finishSaved();
      }
    } catch (finishError) {
      if (!isCurrent()) return;
      try {
        const canonicalActivity = await backendService.fetchActivity?.(activityId);
        if (!isCurrent()) return;
        if (canonicalActivity?.status === 'complete') {
          setLocalActivity(canonicalActivity);
          activityRef.current = canonicalActivity;
          providerRef.current?.stop?.();
          providerRef.current = null;
          await finishSaved();
          return;
        }
      } catch {
        // Preserve the original finish error only for its still-current owner.
      }
      if (!isCurrent()) return;
      completingActivityRef.current = false;
      setCompletionError(finishError.response?.data?.error || finishError.message || 'Could not complete the workout.');
    } finally {
      if (completionRunRef.current === run) completionRunRef.current = null;
      releaseRun();
      if (isCurrent()) { setCompletionPending(false); setCompletionStage(null); setCompletionRetrying(false); }
    }
  }, [backendService, canInteract, onActivityComplete, completionOwner, completionIdentity]);

  const completeWorkStep = useCallback((step, detectedCount) => {
    if (!canInteract) return;
    completionGateRef.current.run(step.workout_set_id, async () => {
      const current = activityRef.current;
      await backendService.updateWorkoutSet(step.workout_set_id, {
        repetitions: detectedCount,
        complete: true,
      }, {
        workoutId: current._id,
        workoutExerciseId: step.workout_exercise_id,
        workoutStatus: current.status,
      });
      await backendService.waitForWorkoutSetUpdates?.();
      const refreshed = await backendService.fetchActivity(current._id);
      setLocalActivity(refreshed);
      activityRef.current = refreshed;
      onActivityRefresh?.(refreshed);
      if (deriveCircuitPosition(refreshed, Date.now()).kind === 'complete') await finishWorkout();
      return refreshed;
    }).catch(writeError => setError(writeError.response?.data?.error || writeError.message || 'Could not save the detected set.'));
  }, [backendService, canInteract, finishWorkout, onActivityRefresh]);

  useEffect(() => {
    if (canInteract && localActivity.status === 'ongoing' && position.kind === 'work' && position.remainingSeconds === 0) {
      completeWorkStep(position, repState.count);
    }
  }, [canInteract, completeWorkStep, localActivity.status, position, repState.count]);

  observationHandlerRef.current = observation => {
    if (!canInteract) return;
    drawOverlay(canvasRef.current, videoRef.current, observation);
    const currentPosition = positionRef.current;
    if (activityRef.current?.status !== 'ongoing' || currentPosition?.kind !== 'work' || !counterRef.current) return;
    const next = counterRef.current.update(observation);
    if (next.lastRep && next.lastRep !== surfacedRepRef.current) {
      surfacedRepRef.current = next.lastRep;
      setFormFeedback(next.lastRep.warnings?.includes('body_line_deviation')
        ? 'Keep shoulders, hips, knees, and ankles in one strong line.'
        : '');
    }
    setRepState(previous => (
      previous.count === next.count && previous.phase === next.phase && previous.selectedSide === next.selectedSide
        ? previous
        : next
    ));
    if (next.count >= currentPosition.target_repetitions) completeWorkStep(currentPosition, next.count);
  };

  const prepareCamera = useCallback(async () => {
    if (!canInteract) return;
    setCameraStatus('loading');
    setError('');
    try {
      await providerRef.current?.stop?.();
      const provider = providerFactory();
      providerRef.current = provider;
      const details = await provider.start({
        video: videoRef.current,
        onObservation: observation => observationHandlerRef.current?.(observation),
        onError: () => {
          if (providerRef.current === provider) providerRef.current = null;
          setDiagnostic(null);
          setCameraStatus('error');
          setError('Pose inference stopped. Retry camera to continue this workout.');
        },
      });
      setDiagnostic(details);
      setCameraStatus('ready');
      provider.setInferenceEnabled?.(activityRef.current.status === 'ongoing' && positionRef.current?.kind === 'work');
    } catch (cameraError) {
      setCameraStatus('error');
      setError(errorMessage(cameraError));
    }
  }, [canInteract, providerFactory]);

  const startWorkout = useCallback(async () => {
    if (!canInteract || cameraStatus !== 'ready') return;
    setError('');
    try {
      const started = await backendService.startWorkout(activityRef.current._id);
      setLocalActivity(started);
      activityRef.current = started;
      onActivityRefresh?.(started);
    } catch (startError) {
      setError(startError.response?.data?.error || startError.message || 'Could not start the workout.');
    }
  }, [backendService, cameraStatus, canInteract, onActivityRefresh]);

  const stopCamera = useCallback(async () => {
    await providerRef.current?.stop?.();
    providerRef.current = null;
    setCameraStatus('idle');
    setDiagnostic(null);
    if (canvasRef.current && process.env.NODE_ENV !== 'test') canvasRef.current.getContext('2d')?.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
  }, []);

  useEffect(() => {
    if (!canInteract) void stopCamera();
  }, [canInteract, stopCamera]);

  useEffect(() => {
    const previousStatus = previousStatusRef.current;
    previousStatusRef.current = localActivity.status;
    if (previousStatus === 'ongoing' && localActivity.status !== 'ongoing') void stopCamera();
  }, [localActivity.status, stopCamera]);

  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden) {
        providerRef.current?.setInferenceEnabled?.(false);
        if (cameraStatus === 'ready') setCameraStatus('paused');
      } else if (cameraStatus === 'paused') {
        setCameraStatus('ready');
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [cameraStatus]);

  useEffect(() => {
    onReadyRef.current?.();
    return () => { providerRef.current?.stop?.(); };
  }, []);

  useEffect(() => {
    if (canInteract && localActivity.status === 'ongoing' && position.kind === 'complete') finishWorkout();
  }, [canInteract, finishWorkout, localActivity.status, position.kind]);

  if (!poseMetadata?.supported) {
    return <div className="ai-supervised-error" role="alert">Pushups pose metadata is unavailable. Use a normal workout.</div>;
  }

  const completion = { pending: completionPending, stage: completionStage, saved: completionSaved };
  // The announcement stays the first child so it survives the completed-status branch swap.
  const completionAnnouncement = <SubmitFeedbackAnnouncement feedback={completion} noun={SUBMIT_NOUNS.workout} />;
  const completionProgress = (completionPending || completionSaved) && (
    <SubmitFeedbackProgress className="ai-supervised-completion-progress" feedback={completion} noun={SUBMIT_NOUNS.workout} />
  );
  const refreshAlert = refreshError && <div className="ai-supervised-error" role="alert">{refreshError}</div>;

  if (!canInteract) {
    return (
      <section className="ai-supervised-player" aria-label="AI-supervised Pushups workout">
        {completionAnnouncement}
        <p className="ai-supervised-eyebrow">AI-supervised workout · PoC</p>
        {completionProgress ? <div className="ai-supervised-actions">{completionProgress}</div> : (
          <p className="ai-supervised-setup">
            {readOnly
              ? 'AI supervision controls are available only to the workout owner.'
              : 'This AI-supervised workout is not currently interactive.'}
          </p>
        )}
        {refreshAlert}
      </section>
    );
  }

  const isPending = localActivity.status === 'pending';
  const isRest = position.kind === 'rest';
  const isWork = position.kind === 'work';
  const diagnosticLabel = diagnostic && (diagnostic.verifiedBackend
    ? `${diagnostic.verifiedBackend} · ${diagnostic.provider}`
    : `Web fallback · ${diagnostic.requestedDelegate || 'runtime'} requested · ${diagnostic.provider}`);
  const totalRounds = (localActivity.data?.ai_supervision?.circuit?.steps || []).filter(step => step.kind === 'work').length;

  return (
    <section className="ai-supervised-player" aria-label="AI-supervised Pushups workout">
      {completionAnnouncement}
      <div className="ai-supervised-header">
        <div>
          <p className="ai-supervised-eyebrow">AI-supervised workout · PoC</p>
          <h4>{isRest ? 'Rest' : isWork ? `Round ${position.round} of ${totalRounds}` : 'Pushups complete'}</h4>
        </div>
        {diagnosticLabel && <span className="ai-supervised-backend">{diagnosticLabel}</span>}
      </div>

      <div className="ai-supervised-stage">
        <video ref={videoRef} muted playsInline aria-label="Local camera preview for Pushup pose detection" />
        <canvas ref={canvasRef} aria-label="Detected Pushup pose landmarks" />
        {cameraStatus === 'idle' && <div className="ai-supervised-stage-placeholder">Camera is off</div>}
        {cameraStatus === 'loading' && <div className="ai-supervised-stage-placeholder">Loading camera and pose model…</div>}
        {cameraStatus === 'paused' && <div className="ai-supervised-stage-placeholder">Inference paused while Kata.fit is hidden</div>}
        {isRest && cameraStatus === 'ready' && <div className="ai-supervised-rest-overlay"><strong>{position.remainingSeconds}</strong><span>seconds rest</span></div>}
      </div>

      {isWork && localActivity.status === 'ongoing' && (
        <div className="ai-supervised-scoreboard">
          <div><strong>{repState.count}</strong><span>of {position.target_repetitions} reps</span></div>
          <div><strong>{repState.selectedSide || '—'}</strong><span>tracked side</span></div>
          <div><strong>{repState.phase.replaceAll('_', ' ')}</strong><span>movement phase</span></div>
          {Number.isFinite(position.remainingSeconds) && <div><strong>{position.remainingSeconds}s</strong><span>work time left</span></div>}
        </div>
      )}
      {formFeedback && <p className="ai-supervised-form-feedback" role="status">{formFeedback}</p>}
      <div className="sr-only" aria-live="polite">{repState.count ? `${repState.count} Pushups counted` : ''}</div>

      {isPending && (
        <div className="ai-supervised-setup">
          <p>Place your phone side-on and keep shoulders, wrists, hips, knees, and ankles visible.</p>
          {referenceImages.length === 2 && <div className="ai-supervised-reference-images">{referenceImages.map((src, index) => <img key={src} src={src} alt={index === 0 ? 'Pushups top position reference' : 'Pushups bottom position reference'} />)}</div>}
        </div>
      )}

      <p className="ai-supervised-privacy">Frames stay on this device for pose processing and are not saved by this PoC. The model asset is downloaded from Google; optional livestreaming is separate.</p>
      {diagnostic?.fallbackReason && <p className="ai-supervised-note">GPU unavailable; using CPU fallback.</p>}
      {error && <div className="ai-supervised-error" role="alert">{error}</div>}
      {completionError && <div className="ai-supervised-error" role="alert">{completionError}</div>}
      {refreshAlert}

      <div className="ai-supervised-actions">
        {position.kind === 'complete' && (completionError || completionRetrying) ? (
          <SubmitFeedbackButton
            className="btn-select btn-emphasis"
            label="Retry completion"
            icon={<ArrowPathIcon />}
            feedback={completion}
            noun={SUBMIT_NOUNS.workout}
            onClick={() => finishWorkout(true)}
          />
        ) : completionProgress}
        {position.kind !== 'complete' && cameraStatus !== 'ready' && cameraStatus !== 'loading' && <button type="button" className="btn-select" onClick={prepareCamera}>{cameraStatus === 'error' ? 'Retry camera' : 'Prepare camera'}</button>}
        {position.kind !== 'complete' && cameraStatus === 'loading' && <button type="button" className="btn-select" disabled>Preparing…</button>}
        {position.kind !== 'complete' && isPending && <button type="button" className="btn-select btn-emphasis" disabled={cameraStatus !== 'ready'} onClick={startWorkout}>Start AI workout</button>}
        {position.kind !== 'complete' && (cameraStatus === 'ready' || cameraStatus === 'paused') && <button type="button" className="btn-cancel" onClick={stopCamera}>Stop camera</button>}
      </div>
    </section>
  );
}
